import StarsIcon from "@mui/icons-material/Stars";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import {
  Award,
  Calendar,
  MapPin,
  MessageSquare,
  Pen,
  Star,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { Pagination } from "rsuite";
import {
  Education,
  Experience,
  Review,
  User,
} from "../../../../../../models/user";
import { getSpecializationLabelKey } from "../../../../../../models/specializationConstants";
import { AxiosHttpClient } from "../../../../../../services/AxiosHttpService";
import { FlexContainer } from "../../../../../../styles/FlexContainer";
import { useAuth } from "../../../../../Reusable/AuthProvider";
import ButtonPrimary from "../../../../../Reusable/ButtonPrimary";
import ButtonSecondary from "../../../../../Reusable/ButtonSecondary";
import ExpertVerifiedBadge from "../../../../../Reusable/ExpertVerifiedBadge";
import { useChat } from "../../../../../Reusable/UseUserChat";
import {
  getPresenceMessage,
  getRelativeTime,
} from "../../../../../Reusable/Utils";
import SubmitReview from "../SubmitReview";

interface ExpertPageProps {
  user: User | null;
}

const ExpertPage: React.FC<ExpertPageProps> = ({ user }) => {
  const getReviewsPage = () => {
    if (previousController.current) {
      previousController.current.abort();
    }
    const controller = new AbortController();
    const signal = controller.signal;
    previousController.current = controller;
    AxiosHttpClient.get<any>(
      "api/v1/docuveritas/users/reviews/page",
      {
        criteriaList:
          "expert_id=" +
          params.userId +
          (starFilter !== "none" ? ",rating=" + starFilter : ""),
        pageNumber: page - 1,
        pageSize: limit,
        sort: "createdAt",
        order: "desc",
      },
      signal
    )
      .then((res) => {
        setReviews(res.content);
        setTotalElements(res.totalElements);
      })
      .catch((err) => {});
  };

  const previousController = useRef<AbortController>(null!);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user: userConnected } = useAuth();
  const params = useParams<{ userId: string }>();
  const { openChat } = useChat();
  const [selectedTab, setSelectedTab] = useState("experience");
  const [popupReview, setPopupReview] = useState("");

  const limit = 5;

  const [page, setPage] = useState(1);

  const [totalElements, setTotalElements] = useState(0);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [starFilter, setStarFilter] = useState("none");
  const [canReview, setCanReview] = useState(false);

  useEffect(() => {
    if (!userConnected?.id) return;
    AxiosHttpClient.get<boolean>(
      `api/v1/docuveritas/users/can-review/${params.userId}`
    )
      .then((res) => setCanReview(res))
      .catch(() => setCanReview(false));
  }, [params.userId, userConnected?.id]);

  useEffect(() => {
    getReviewsPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, starFilter]);

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, starFilter]);

  // derived values
  const averageRating = user?.averageRating || 0;

  const reviewCount = user?.reviewsCount || 0;

  const yearsOfExperience = useMemo(() => {
    if (!user?.expertDetails?.experiences?.length) return 0;
    // find earliest startDate
    const dates = user.expertDetails.experiences
      .map((e) => dayjs(e.startDate))
      .filter((d): d is Dayjs => !!d);
    if (!dates.length) return 0;
    const earliest = dates.reduce(
      (min, d) => (d.isBefore(min) ? d : min),
      dates[0]
    );
    return dayjs().diff(earliest, "year");
  }, [user]);

  if (!user) {
    return <Typography>Loading…</Typography>;
  }

  return (
    <Box
      sx={{
        maxWidth: 1200,
        width: "100%",
        mx: "auto",
        py: 6,
        backgroundColor: "hsl(var(--background))",
        px: { xs: 4, md: 6 },
      }}
    >
      {/* Profile Header */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 4,
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ position: "relative" }}>
              <Avatar
                src={user.imageUrl || undefined}
                alt={`${user.firstName} ${user.lastName}`}
                sx={{
                  width: 96,
                  height: 96,
                  border: "2px solid",
                  borderColor: "grey.200",
                }}
              />
              <Box
                title={
                  user.connected
                    ? "Connecté"
                    : user.lastSeen
                    ? "Connecté " + getRelativeTime(user.lastSeen, t)
                    : "Hors ligne"
                }
                sx={{
                  position: "absolute",
                  bottom: 4,
                  right: 4,
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  bgcolor: user.connected ? "success.main" : "grey.400",
                  border: "2px solid",
                  borderColor: "common.white",
                }}
              />
            </Box>
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{ color: "hsl(var(--foreground))" }}
                >
                  {user.title} {user.firstName} {user.lastName}
                </Typography>
                <ExpertVerifiedBadge
                  status={user.expertDetails?.verificationStatus}
                />
              </Box>
              <Typography
                variant="subtitle1"
                sx={{ color: "hsl(var(--muted-foreground))" }}
              >
                {user.expertDetails?.professionalTitle}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "hsl(var(--muted-foreground))",
                }}
              >
                <MapPin
                  style={{
                    height: "1rem",
                    width: "1rem",
                    color: "hsl(var(--muted-foreground))",
                  }}
                />
                <Typography sx={{ color: "hsl(var(--muted-foreground))" }}>
                  {user.location?.name}, {user.location?.countryCode}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Star
                    style={{
                      height: "1rem",
                      width: "1rem",
                      color: "#facc15",
                      fill: "#facc15",
                    }}
                  />
                  <Typography
                    fontWeight="medium"
                    sx={{ color: "hsl(var(--foreground))" }}
                  >
                    {averageRating.toFixed(1)}
                  </Typography>
                  <Typography sx={{ color: "hsl(var(--muted-foreground))" }}>
                    ({reviewCount} reviews)
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Calendar
                    style={{
                      height: "1rem",
                      width: "1rem",
                      color: "hsl(var(--muted-foreground))",
                    }}
                  />
                  <Typography sx={{ color: "hsl(var(--muted-foreground))" }}>
                    {yearsOfExperience} years experience
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Award
                    style={{
                      height: "1rem",
                      width: "1rem",
                      color: "hsl(var(--muted-foreground))",
                    }}
                  />
                  <Typography sx={{ color: "hsl(var(--muted-foreground))" }}>
                    {user.certificationsCount} certifications
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
          sx={{ display: "flex", flexDirection: "column", gap: 1 }}
        >
          <ButtonPrimary
            variant="contained"
            startIcon={
              user.id === userConnected?.id ? <Pen /> : <MessageSquare />
            }
            onClick={() => {
              if (user.id === userConnected?.id)
                navigate(`/feature/profile/edit`);
              else openChat([user.id, userConnected?.id].sort().join("_"));
            }}
            sx={{ textTransform: "none" }}
          >
            {user.id === userConnected?.id
              ? t("EXPERT_PAGE_BUTTON_EDIT_PROFILE")
              : t("EXPERT_PAGE_BUTTON_SEND_MESSAGE")}
          </ButtonPrimary>
          {user.id !== userConnected?.id && canReview && (
            <ButtonSecondary
              variant="outlined"
              startIcon={<StarsIcon fontSize="small" />}
              onClick={() => {
                setPopupReview(user.id);
              }}
              sx={{ textTransform: "none" }}
            >
              {t("EXPERT_PAGE_BUTTON_SUBMIT_REVIEW")}
            </ButtonSecondary>
          )}
        </Grid>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid
          item
          xs={12}
          md={8}
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          {/* About */}
          <Card
            sx={{
              border: "1px solid hsl(var(--border))",
              borderRadius: 2,
              boxShadow: "none",
              backgroundColor: "hsl(var(--card))",
            }}
          >
            <CardHeader
              title={t("EXPERT_PAGE_CARDHEADER_ABOUT")}
              sx={{ color: "hsl(var(--foreground))" }}
            />
            <CardContent>
              <Typography
                sx={{
                  whiteSpace: "pre-wrap",
                  color: "hsl(var(--foreground))",
                }}
              >
                {user.expertDetails?.description}
              </Typography>
            </CardContent>
          </Card>

          {/* Specializations */}
          <Card
            sx={{
              border: "1px solid hsl(var(--border))",
              borderRadius: 2,
              boxShadow: "none",
              backgroundColor: "hsl(var(--card))",
            }}
          >
            <CardHeader
              title={t("EXPERT_PAGE_CARDHEADER_SPECIALIZATIONS")}
              sx={{ color: "hsl(var(--foreground))" }}
            />
            <CardContent>
              <FlexContainer flexWrap="wrap" gap={1}>
                {user.expertDetails?.specialties.map((s) => (
                  <Chip
                    key={s}
                    label={t(getSpecializationLabelKey(s))}
                    variant="outlined"
                    sx={{
                      fontSize: "14px",
                      borderColor: "hsl(var(--border))",
                      color: "hsl(var(--foreground))",
                    }}
                  />
                ))}
              </FlexContainer>
            </CardContent>
          </Card>

          {/* Experience & Education Tabs */}
          <Box>
            <Box
              sx={{
                display: "flex",
                borderRadius: "8px",
                backgroundColor: "hsl(var(--muted))",
                padding: "4px",
                height: "40px",
              }}
            >
              {[
                { label: t("EXPERT_PAGE_TAB_EXPERIENCE"), value: "experience" },
                { label: t("EXPERT_PAGE_TAB_EDUCATION"), value: "education" },
              ].map((tab) => (
                <Button
                  onClick={() => {
                    setSelectedTab(tab.value);
                  }}
                  disableElevation
                  sx={{
                    flex: 1,
                    textTransform: "none",
                    paddingX: "12px",
                    paddingY: "6px",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    borderRadius: "6px",
                    transition: "all 0.2s ease-in-out",
                    backgroundColor:
                      selectedTab === tab.value
                        ? "hsl(var(--background))"
                        : "transparent",
                    color:
                      selectedTab === tab.value
                        ? "hsl(var(--foreground))"
                        : "hsl(var(--muted-foreground))",
                    boxShadow:
                      selectedTab === tab.value
                        ? "0 1px 2px 0 rgb(0 0 0 / 0.05)"
                        : "none",
                    "&:hover": {
                      backgroundColor:
                        selectedTab === tab.value
                          ? "hsl(var(--background))"
                          : "transparent",
                      color:
                        selectedTab === tab.value
                          ? "hsl(var(--foreground))"
                          : "hsl(var(--foreground))",
                    },
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </Box>

            {selectedTab === "experience" && (
              <Box mt={2}>
                <Card
                  sx={{
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 2,
                    boxShadow: "none",
                    backgroundColor: "hsl(var(--card))",
                  }}
                >
                  <CardContent sx={{ pt: 3 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {user.expertDetails?.experiences.map(
                        (exp: Experience) => (
                          <Box
                            key={exp.id}
                            sx={{
                              position: "relative",
                              pl: 2,
                              borderLeft: "2px solid",
                              borderColor: "hsl(var(--border))",
                              pb: 1,
                            }}
                          >
                            <Box
                              sx={{
                                position: "absolute",
                                left: -7,
                                top: 6,
                                width: 12,
                                height: 12,
                                bgcolor: "hsl(var(--border))",
                                borderRadius: "50%",
                              }}
                            />
                            <Typography
                              fontWeight="medium"
                              sx={{ color: "hsl(var(--foreground))" }}
                            >
                              {exp.jobTitle}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: "hsl(var(--muted-foreground))" }}
                            >
                              {exp.companyName}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: "hsl(var(--muted-foreground))" }}
                            >
                              {dayjs(exp.startDate)?.format("MM/YYYY")} –{" "}
                              {exp.current
                                ? "Present"
                                : dayjs(exp.endDate)?.format("MM/YYYY")}
                            </Typography>
                          </Box>
                        )
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            )}

            {selectedTab === "education" && (
              <Box mt={2}>
                <Card
                  sx={{
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 2,
                    boxShadow: "none",
                    backgroundColor: "hsl(var(--card))",
                  }}
                >
                  <CardContent sx={{ pt: 3 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {user.expertDetails?.educations.map((ed: Education) => (
                        <Box
                          key={ed.id}
                          sx={{
                            position: "relative",
                            pl: 2,
                            borderLeft: "2px solid hsl(var(--border))",
                            pb: 1,
                          }}
                        >
                          <Box
                            sx={{
                              position: "absolute",
                              left: -7,
                              top: 6,
                              width: 12,
                              height: 12,
                              bgcolor: "hsl(var(--border))",
                              borderRadius: "50%",
                            }}
                          />
                          <Typography
                            fontWeight="medium"
                            sx={{ color: "hsl(var(--foreground))" }}
                          >
                            {ed.degreeName}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "hsl(var(--muted-foreground))" }}
                          >
                            {ed.institution}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "hsl(var(--muted-foreground))" }}
                          >
                            {ed.endYear}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            )}
          </Box>

          {/* Reviews */}
          <Card
            sx={{
              border: "1px solid hsl(var(--border))",
              borderRadius: 2,
              boxShadow: "none",
              backgroundColor: "hsl(var(--card))",
            }}
          >
            <CardHeader
              title={t("EXPERT_PAGE_CARDHEADER_REVIEWS")}
              sx={{ color: "hsl(var(--foreground))" }}
              action={
                <Box sx={{ display: "flex", gap: 1 }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Button
                      key={star}
                      size="small"
                      variant={
                        starFilter === star.toString()
                          ? "contained"
                          : "outlined"
                      }
                      onClick={() => {
                        starFilter === star.toString()
                          ? setStarFilter("none")
                          : setStarFilter(star.toString());
                        setPage(1);
                      }}
                      sx={{
                        minWidth: 0,
                        borderColor: "hsl(var(--border))",
                        color: "hsl(var(--foreground))",
                        "&:hover": {
                          borderColor: "hsl(var(--primary))",
                          backgroundColor: "hsl(var(--muted))",
                        },
                        ...(starFilter === star.toString() && {
                          bgcolor: "#facc15",
                          borderColor: "#facc15",
                          color: "#fff",
                          "&:hover": {
                            bgcolor: "#eab308",
                            borderColor: "#eab308",
                          },
                        }),
                      }}
                    >
                      {star} ★
                    </Button>
                  ))}
                </Box>
              }
            />
            <CardContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {reviews.map((r: Review, index) => (
                  <>
                    <Box key={r.id} sx={{ borderBottom: "1px solid hsl(var(--border))" }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            fontWeight="medium"
                            sx={{ color: "hsl(var(--foreground))" }}
                          >
                            {r.author.firstName} {r.author.lastName}
                          </Typography>
                          <Box sx={{ display: "flex" }}>
                            {Array(5)
                              .fill(0)
                              .map((_, i) => (
                                <Star
                                  key={i}
                                  style={{
                                    height: "1rem",
                                    width: "1rem",
                                    color:
                                      i < r.rating
                                        ? "#facc15"
                                        : "hsl(var(--muted-foreground))",
                                    fill: i < r.rating ? "#facc15" : "white",
                                  }}
                                />
                              ))}
                          </Box>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{ color: "hsl(var(--muted-foreground))" }}
                        >
                          {r.createdAt
                            ? dayjs(r.createdAt).format("YYYY-MM-DD")
                            : ""}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: "pre-wrap",
                          color: "hsl(var(--foreground))",
                        }}
                      >
                        {r.comment}
                      </Typography>
                    </Box>
                    {index < reviews.length - 1 && <Divider />}
                  </>
                ))}
              </Box>
              <Box 
                sx={{ 
                  marginTop: "16px",
                  "& .rs-pagination": {
                    "& .rs-pagination-btn": {
                      backgroundColor: "hsl(var(--background))",
                      borderColor: "hsl(var(--border))",
                      color: "hsl(var(--foreground))",
                      "&:hover": {
                        backgroundColor: "hsl(var(--muted))",
                        borderColor: "hsl(var(--primary))",
                      },
                    },
                    "& .rs-pagination-btn-active": {
                      backgroundColor: "hsl(var(--primary))",
                      borderColor: "hsl(var(--primary))",
                      color: "hsl(var(--primary-foreground))",
                    },
                  },
                }}
              >
                <Pagination
                  prev
                  next
                  first
                  last
                  ellipsis
                  boundaryLinks
                  maxButtons={3}
                  size="sm"
                  layout={["pager"]}
                  total={totalElements}
                  limitOptions={[10, 30, 50]}
                  limit={limit}
                  activePage={page}
                  onChangePage={setPage}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column */}
        <Grid
          item
          xs={12}
          md={4}
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          {/* Credentials */}
          <Card
            sx={{
              border: "1px solid hsl(var(--border))",
              borderRadius: 2,
              boxShadow: "none",
              backgroundColor: "hsl(var(--card))",
            }}
          >
            <CardHeader
              title={t("EXPERT_PAGE_CARDHEADER_CREDENTIALS")}
              sx={{ color: "hsl(var(--foreground))" }}
            />
            <CardContent
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "hsl(var(--muted-foreground))" }}
                >
                  {t("EXPERT_PAGE_LABEL_BAR_ASSOCIATION")}
                </Typography>
                <Typography
                  fontWeight="medium"
                  sx={{ color: "hsl(var(--foreground))" }}
                >
                  {user.expertDetails?.barAssociation}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "hsl(var(--muted-foreground))" }}
                >
                  {t("EXPERT_PAGE_LABEL_LANGUAGES")}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {user.expertDetails?.languages.map((l) => (
                    <Chip
                      key={l}
                      label={l}
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: "hsl(var(--border))",
                        color: "hsl(var(--foreground))",
                      }}
                    />
                  ))}
                </Box>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "hsl(var(--muted-foreground))" }}
                >
                  {t("EXPERT_PAGE_LABEL_JURISDICTIONS")}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {user.expertDetails?.jurisdictions.map((j) => (
                    <Chip
                      key={j.id}
                      label={`${j.name}, ${j.countryCode}`}
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: "hsl(var(--border))",
                        color: "hsl(var(--foreground))",
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Availability */}
          <Card
            sx={{
              border: "1px solid hsl(var(--border))",
              borderRadius: 2,
              boxShadow: "none",
              backgroundColor: "hsl(var(--card))",
            }}
          >
            <CardHeader
              title="Availability"
              sx={{ color: "hsl(var(--foreground))" }}
            />
            <CardContent
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: user.connected ? "success.main" : "grey.400",
                  }}
                />
                <Typography
                  fontWeight="medium"
                  sx={{ color: "hsl(var(--foreground))" }}
                >
                  {getPresenceMessage(user, t)}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{ color: "hsl(var(--muted-foreground))" }}
              >
                {t("EXPERT_PAGE_AVAILABILITY_RESPOND_TIME", {
                  delay: "24 heures",
                })}
              </Typography>
              <ButtonPrimary
                variant="contained"
                startIcon={<MessageSquare />}
                onClick={() => {
                  openChat([user.id, userConnected?.id].sort().join("_"));
                }}
                sx={{ textTransform: "none" }}
              >
                {t("EXPERT_PAGE_BUTTON_SEND_MESSAGE")}
              </ButtonPrimary>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {popupReview && (
        <SubmitReview
          popupActive={popupReview}
          setPopupActive={setPopupReview}
          callback={getReviewsPage}
        />
      )}
    </Box>
  );
};

export default ExpertPage;
